// Kas & Bank Classification — Dialog upsert klasifikasi 1 mutasi (2026-02).
import { useEffect, useMemo, useState } from "react";
import { X, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";
import {
  KIND_META, KIND_ORDER, allowedKinds, upsertClassification, deleteClassification, fetchCOA,
  suggestCOA, buildCoaGroupsWithSuggestions, buildProjectOptions,
} from "@/lib/kasBankClassification";
import SearchableSelect from "@/components/SearchableSelect";

export default function ClassificationDialog({ row, defaultKind, onClose, onSaved, canWrite = true, canDelete = true }) {
  const existing = row?.klasifikasi || null;
  const allowed = useMemo(() => allowedKinds(row?.movement_id), [row?.movement_id]);
  const [kind, setKind] = useState(existing?.kind || (allowed.includes(defaultKind) ? defaultKind : allowed[0] || "penerimaan"));
  const [coaId, setCoaId] = useState(existing?.coa_id || "");
  const [projectId, setProjectId] = useState(existing?.project_id || "");
  const [docNo, setDocNo] = useState(existing?.doc_no || "");
  const [note, setNote] = useState(existing?.note || "");
  const [coa, setCoa] = useState([]);
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  // 2026-02 (user request): saran COA otomatis dari kata kunci COA.
  const [suggested, setSuggested] = useState([]);

  useEffect(() => { fetchCOA().then(setCoa).catch(() => setCoa([])); }, []);
  useEffect(() => { api.get("/active-projects").then((r) => setProjects(Array.isArray(r.data) ? r.data : [])).catch(() => setProjects([])); }, []);
  useEffect(() => {
    let cancelled = false;
    const desc = row?.description || "";
    if (!desc.trim()) return;
    suggestCOA([desc]).then((s) => {
      if (cancelled) return;
      const list = s[desc] || [];
      setSuggested(list);
      // Auto-preselect hanya utk klasifikasi baru (tanpa `existing`).
      if (!existing && list.length > 0 && !coaId) setCoaId(list[0].coa_id);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.description]);

  if (!row) return null;
  const disabled = !row?.movement_id;

  const submit = async (e) => {
    e.preventDefault();
    if (disabled) return;
    try {
      setSaving(true);
      const saved = await upsertClassification({
        movement_id: row.movement_id,
        kind,
        coa_id: coaId || null,
        project_id: projectId || null,
        doc_no: docNo || null,
        note: note || null,
      });
      toast.success("Klasifikasi disimpan.");
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan klasifikasi.");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!existing) return;
    if (!window.confirm("Hapus klasifikasi ini? Mutasi akan kembali menjadi 'Belum Diklasifikasi'.")) return;
    try {
      await deleteClassification(row.movement_id);
      toast.success("Klasifikasi dihapus.");
      onSaved?.(null);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus klasifikasi.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="kbc-dialog">
      <form onSubmit={submit} className="bg-white w-full max-w-lg border border-slate-200 shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <div className="overline text-[10px] text-slate-500 tracking-widest">KLASIFIKASI MUTASI</div>
            <div className="font-display text-base font-semibold text-[#0B1D3A]">{existing ? "Ubah Klasifikasi" : "Tambah Klasifikasi"}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100" data-testid="kbc-close"><X size={16} /></button>
        </div>

        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/70 grid grid-cols-2 gap-2 text-[11px]">
          <div><div className="text-slate-500">Tanggal</div><div className="font-medium tabular-nums">{fmtDate(row.date) || "—"}</div></div>
          <div><div className="text-slate-500">Arah</div><div className={`font-semibold ${row.direction === "in" ? "text-emerald-700" : "text-rose-700"}`}>{row.direction === "in" ? "Masuk" : "Keluar"}</div></div>
          <div className="col-span-2"><div className="text-slate-500">Deskripsi</div><div className="font-medium text-slate-800">{row.description || "—"}</div></div>
          <div className="col-span-2"><div className="text-slate-500">Rekening</div><div className="font-medium">{row.account_bank_name} · {row.account_no}</div></div>
          <div><div className="text-slate-500">Nominal</div><div className={`font-bold tabular-nums ${row.direction === "in" ? "text-emerald-700" : "text-rose-700"}`}>{formatIDR(row.amount || 0)}</div></div>
          <div><div className="text-slate-500">Proyek</div><div className="font-medium">{row.project_name || "—"}</div></div>
        </div>

        {disabled ? (
          <div className="p-4 text-[11px] text-amber-800 bg-amber-50 border-b border-amber-200">
            Mutasi ini tidak memiliki ID stabil (data legacy). Klasifikasi belum tersedia untuk baris ini.
          </div>
        ) : (
          <div className="p-4 space-y-3 text-[11px]">
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Jenis</div>
              <div className="flex items-center gap-1 flex-wrap" data-testid="kbc-kind">
                {KIND_ORDER.map((k) => {
                  const disabledK = !allowed.includes(k);
                  const active = kind === k;
                  return (
                    <button key={k} type="button" disabled={disabledK} onClick={() => setKind(k)} data-testid={`kbc-kind-${k}`}
                      className={`px-3 py-1 border text-[10px] uppercase tracking-wider font-semibold transition-colors ${active ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : disabledK ? "bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed" : "bg-white text-slate-700 border-slate-300 hover:border-[#0B1D3A]"}`}>
                      {KIND_META[k].label}
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Arah mutasi menentukan pilihan yang boleh: masuk = Penerimaan/Transfer · keluar = Pembayaran/Transfer.</div>
            </div>

            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Kategori Akun (COA)</div>
              {/* 2026-09-24 (user request): dropdown searchable — ketik utk menyaring. */}
              {/* 2026-02 (user request): tampilkan grup "Disarankan" berdasarkan kata kunci COA. */}
              <SearchableSelect value={coaId} onChange={setCoaId}
                groups={buildCoaGroupsWithSuggestions(suggested, coa)}
                placeholder="— Tanpa Kategori —" testid="kbc-coa" />
              {suggested.length > 0 && (
                <div className="text-[10px] text-emerald-700 mt-1" data-testid="kbc-suggest-hint">
                  🔮 Disarankan: <b>{suggested[0].code} · {suggested[0].name}</b> (cocok kata kunci "{suggested[0].keyword}")
                </div>
              )}
            </div>

            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Proyek Terkait (opsional — override)</div>
              <SearchableSelect value={projectId} onChange={setProjectId}
                options={buildProjectOptions(projects, "— Ikuti data asal —")}
                placeholder="— Ikuti data asal —" testid="kbc-project" />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Nomor Dokumen (opsional)</div>
                <input value={docNo} onChange={(e) => setDocNo(e.target.value)} data-testid="kbc-docno"
                  placeholder="Manual, mis. BM-2026/01" maxLength={64}
                  className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
              </div>
              <div>
                <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Catatan (opsional)</div>
                <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="kbc-note"
                  placeholder="Catatan bebas" maxLength={200}
                  className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
              </div>
            </div>

            {existing && (
              <div className="text-[10px] text-slate-500 italic">
                Terakhir diubah: {existing.updated_at ? fmtDate(existing.updated_at) : "—"} oleh {existing.updated_by_name || "—"}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/70">
          <div>
            {existing && canDelete && (
              <button type="button" onClick={remove} data-testid="kbc-delete"
                className="inline-flex items-center gap-1 px-3 py-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50 text-[10px] uppercase tracking-wider font-semibold">
                <Trash2 size={12} /> Hapus Klasifikasi
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} data-testid="kbc-cancel"
              className="px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100 text-[10px] uppercase tracking-wider font-semibold">Batal</button>
            <button type="submit" disabled={saving || disabled || !canWrite} data-testid="kbc-save"
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">
              <Save size={12} /> {saving ? "Menyimpan…" : "Simpan"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
